import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Popconfirm, Space, Spin, Switch, Tag, Typography, message } from 'antd';
import { useApi } from '../../hooks/use_api';
import { useNodeApps } from '../../hooks/use_node_apps';
import useIsAdmin from '../../hooks/use_is_admin';
import EmbedControls from '../Embed/EmbedControls';
import { useNotifications } from '../../hooks/use_notifications';
import PageApp from './PageApp';
import ChannelApp from './ChannelApp';
import GatheringApp from './GatheringApp';
import AddHostModal from './AddHostModal';
import './AppPage.css';

const { Title, Text } = Typography;

// One installed app instance (/app/:instanceId). The header is the same for
// every app: where it is installed — its entity hosts, and the node itself
// when a superadmin installed it there — and below it, the app's own view.
// Apps without a web view yet show their manifest. An admin of the app can
// install it in further entities from here (AddHostModal): one instance, any
// number of hosts, which is how a channel comes to span several groups.
const VIEWS = { page: PageApp, channel: ChannelApp, gathering: GatheringApp };

function AppPage() {
    const { instanceId } = useParams();
    const api = useApi();
    const { refresh: refreshNodeApps } = useNodeApps();
    const { featureNotifications, markEntityRead, fetchPreferences, setPreference } = useNotifications();
    // An app's notifications start unmuted (no preference row = on); the
    // switch writes a row on the instance, which the backend honours.
    const [notifEnabled, setNotifEnabled] = useState(true);
    const [instance, setInstance] = useState(null);
    const [error, setError] = useState(null);
    const [hosts, setHosts] = useState({});
    // The instance is an entity too: its row carries the embed switch
    // (backend services/embed_blocks.py), which its admins — whoever
    // installed it, or administers a host — set from here.
    const [entity, setEntity] = useState(null);
    const [addHostOpen, setAddHostOpen] = useState(false);
    const { isAdmin } = useIsAdmin(instanceId);
    const loadEntity = useCallback(() => api.get(`/api/entities/${instanceId}`).then(setEntity).catch(() => setEntity(null)), [api, instanceId]);

    const load = useCallback(() => api.get(`/api/apps/${instanceId}`)
        .then(inst => {
            setInstance(inst);
            return Promise.all(inst.host_ids.map(id => api.get(`/api/entities/${id}`).catch(() => null)));
        })
        .then(rows => setHosts(Object.fromEntries((rows || []).filter(Boolean).map(e => [e.id, e]))))
        .catch(err => setError(err.status === 403 ? 'You cannot use this app.' : 'Could not load this app.')), [api, instanceId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (isAdmin) loadEntity(); }, [isAdmin, loadEntity]);

    // Opening the app reads its notifications, as opening a channel does.
    useEffect(() => {
        if (!featureNotifications || !instanceId) return;
        markEntityRead(Number(instanceId));
        fetchPreferences().then(prefs => setNotifEnabled(prefs[Number(instanceId)] !== false));
        // markEntityRead changes identity on every poll; run this once per app.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instanceId, featureNotifications]);

    const handleToggleNotif = checked => {
        setNotifEnabled(checked);
        setPreference(Number(instanceId), checked).catch(() => setNotifEnabled(!checked));
    };

    const uninstall = hostId => api.del(`/api/apps/${instanceId}/hosts/${hostId}`)
        .then(() => { message.success('Uninstalled'); return load(); })
        .catch(err => message.error(err.status === 403 ? 'Only an admin of that entity can uninstall it' : 'Could not uninstall'));

    // The node's own install: the nav bar lists it for everyone, so refresh that too.
    const uninstallFromNode = () => api.del(`/api/apps/${instanceId}/node`)
        .then(() => { message.success('Uninstalled from the node'); refreshNodeApps(); return load(); })
        .catch(err => message.error(err.status === 403 ? 'Only a superadmin can uninstall an app from the node' : 'Could not uninstall'));

    if (error) return <div className="app-page"><Alert type="warning" message={error} showIcon /></div>;
    if (!instance) return <div className="app-page"><Spin /></div>;

    const View = VIEWS[instance.kind];
    return (
        <div className="app-page">
            <Space align="center" style={{ marginBottom: 4 }}>
                <Title level={3} style={{ margin: 0 }}>{instance.name}</Title>
                <Tag>{instance.kind}</Tag>
                {featureNotifications && (
                    <Switch size="small" checked={notifEnabled} onChange={handleToggleNotif}
                        checkedChildren="Notifications on" unCheckedChildren="Muted" />
                )}
            </Space>
            <div className="app-page-hosts">
                {!instance.node_wide && instance.host_ids.length === 0 ? 'Not installed anywhere.' : <>Installed in{' '}
                    {instance.node_wide && (
                        <span>
                            <Text strong>this node</Text> (everyone signed in here)
                            {' '}
                            <Popconfirm title="Uninstall from the node?" onConfirm={uninstallFromNode} okText="Uninstall">
                                <Button type="link" size="small" danger style={{ padding: 0 }}>uninstall</Button>
                            </Popconfirm>
                        </span>
                    )}
                    {instance.host_ids.map((id, i) => (
                        <span key={id}>
                            {(i > 0 || instance.node_wide) && ', '}
                            <Link to={`/entity/${id}`}>{hosts[id]?.name || `#${id}`}</Link>
                            {' '}
                            <Popconfirm title="Uninstall from here?" onConfirm={() => uninstall(id)} okText="Uninstall">
                                <Button type="link" size="small" danger style={{ padding: 0 }}>uninstall</Button>
                            </Popconfirm>
                        </span>
                    ))}
                </>}
                {isAdmin && (
                    <>
                        {' '}
                        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setAddHostOpen(true)}>add to another entity</Button>
                    </>
                )}
            </div>
            {isAdmin && (
                <AddHostModal open={addHostOpen} onClose={() => setAddHostOpen(false)} instance={instance} onAdded={load} />
            )}
            {isAdmin && entity && (
                <div className="app-page-embed">
                    <Text type="secondary">Embed</Text>
                    <EmbedControls entity={entity} onSaved={loadEntity} />
                </div>
            )}
            {instance.pending_version && (
                <Alert type="info" showIcon style={{ marginBottom: 16 }}
                    message={`Version ${instance.pending_version} asks for more: ${(instance.pending_scopes || []).filter(s => !instance.approved_scopes.includes(s)).join(', ')}. An admin approves it with “vellby apps approve ${instance.id}”.`} />
            )}
            {View ? <View instance={instance} /> : (
                <Text type="secondary">This app has no view in the web app yet. Its verbs and views are open through the API and MCP.</Text>
            )}
        </div>
    );
}

export default AppPage;
